import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
	DataPlaneMerchant,
	PaymentLabel,
	APSessionResponseFailure,
	APSessionResponseSuccess,
	ApplePaySessionResponse,
	CustomFieldKeys,
	WidgetInputsWithCustomFields,
	MakeApplePayPaymentRequest,
	MakeApplePayPaymentResponse,
	QuickPayRelayResultStatus,
} from '../@types/types';
import { LineItem } from '../components/lineItems/line-items';
import { SuccessTableItem } from '../components/success/success-table';
import { useAgent } from '../newrelic';
import {
	APPLEPAY_ERROR_TYPE,
	ApplePayLocalError,
	ApplePayPaymentPassActivationState,
	getAPInvalidFieldErrorsTranslation,
	getAppleContact,
	getApplePayPaymentRequest,
	getApplePayer,
	getApplePaymentMethodType,
	isApplePayPaymentSuccessful,
	shouldCheckEmail,
} from '../shared/apple-pay-utils';
import { WidgetActions, WidgetErrors, WidgetId } from '../shared/constants';
import { getFundName, getLineItemsArray } from '../shared/utils';

enum APPLEPAY_STATUS {
	IDLE = 'IDLE',
	INITIALISING = 'INITIALISING',
	AVAILABLE = 'AVAILABLE',
	UNAVAILABLE = 'UNAVAILABLE',
}

enum SessionPhase {
	INIT = 'INIT',
	VALIDATING = 'VALIDATING',
	SHEET_SHOWN = 'SHEET_SHOWN',
	PAYMENT_PROCESSING = 'PAYMENT_PROCESSING',
}

export type SuccessInfo = {
	tableItems: SuccessTableItem[];
	emailAddress: string;
	checkEmail?: boolean;
};

const RESPONSE_TIMOUT = 29000;
const SDK_VERSION = 1;
const DEV_HEADERS = {
	'x-or-hs': 'c0fe3a', // Required for development
};

export function useApplePay(config: DataPlaneMerchant, canApplePay: boolean, paymentLabel: PaymentLabel) {
	const agent = useAgent();
	const { t: translation } = useTranslation();
	const initiatedRef = useRef(false);
	const sessionEndedRef = useRef(false);
	const sessionPhaseRef = useRef<SessionPhase>(SessionPhase.INIT);
	const [success, setSuccess] = useState<null | SuccessInfo>(null);
	const [appleError, setAppleError] = useState<ApplePayLocalError | null>(null);
	const [status, setStatus] = useState<APPLEPAY_STATUS>(
		canApplePay ? APPLEPAY_STATUS.IDLE : APPLEPAY_STATUS.UNAVAILABLE
	);

	const {
		Funds,
		MerchantSettings: { MerchantKey, ApplePayMerchantId, ApplePayMerchantName },
	} = config;

	const invalidFieldErrorsTranslation = useMemo(() => getAPInvalidFieldErrorsTranslation(translation), [translation]);

	const validateMerchant = useCallback(
		async (session: ApplePaySession, event: ApplePayJS.ApplePayValidateMerchantEvent) => {
			try {
				const result: ApplePaySessionResponse = await fetch(APP_SETTINGS.applepaySessionUrl, {
					method: 'POST',
					headers: DEV_HEADERS,
					body: JSON.stringify({
						organizationKey: ApplePayMerchantId,
						validationUri: event.validationURL,
						name: ApplePayMerchantName,
						domain: window.location.hostname,
					}),
				})
					.then(res => (res.ok ? res.json() : Promise.reject(res)))
					.catch(error => {
						// eslint-disable-next-line no-console
						console.log('Merchant Validation fetch call:', error); // TODO: temporary. please remove
					});

				// eslint-disable-next-line no-console
				console.log('Merchant Validation fetch returned'); // TODO: temporary. please remove

				if (!result || (result as APSessionResponseFailure).message) {
					throw new Error(
						`Relay service error: ${result ? (result as APSessionResponseFailure).message : ''}`,
						{
							cause: result,
						}
					);
				}

				if (result && (result as APSessionResponseSuccess).success) {
					session.completeMerchantValidation(JSON.parse((result as APSessionResponseSuccess).blob));
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.log('Validate Merchant - Aborting Apple Pay session:', error, session); // TODO: temporary. please modify
				if (!sessionEndedRef.current) session.abort();
				setAppleError({
					type: APPLEPAY_ERROR_TYPE.UNKNOWN,
					message: {
						title: translation('applePayErrors.generic.title'),
						subtitle: translation('applePayErrors.generic.subtitle'),
					},
				});
				agent?.noticeError(WidgetErrors.applePayValidateMerchantError, { error });
			}
		},
		[ApplePayMerchantId, ApplePayMerchantName, agent, translation]
	);

	const confirmPayment = useCallback(
		async (
			session: ApplePaySession,
			event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
			data: WidgetInputsWithCustomFields,
			customFieldKeys: CustomFieldKeys = {}
		) => {
			const multiFunds = getLineItemsArray(data.MultiFunds) as LineItem[];
			const hasMultiFunds = Array.isArray(data.MultiFunds) && data.MultiFunds.length > 0;
			const invalidData = hasMultiFunds ? multiFunds.length === 0 : !data.Amount || !data.FundKeyOrName;
			if (!event.payment || invalidData) {
				setAppleError({
					type: APPLEPAY_ERROR_TYPE.UNKNOWN,
					message: {
						title: translation('applePayErrors.generic.title'),
						subtitle: translation('applePayErrors.generic.subtitle'),
					},
				});
				return;
			}

			let optimisticSuccessTimer: NodeJS.Timeout | null = null;
			const controller = new AbortController();

			try {
				const {
					token: { paymentData, paymentMethod, transactionIdentifier },
					shippingContact,
					billingContact,
				} = event.payment;

				const contact = getApplePayer(shippingContact);

				if (contact.invalid || !contact.payer || !contact.payer.emailAddress) {
					const contactField = contact.missing
						? contact.missing.map(key => translation(`applePayErrors.fields.${key}` as any))
						: undefined;

					setAppleError({
						type: APPLEPAY_ERROR_TYPE.CONTACT_VALIDATION,
						message: {
							title: translation('applePayErrors.MissingContactField.title'),
							subtitle: translation(
								contactField
									? 'applePayErrors.MissingContactField.subtitle'
									: 'applePayErrors.MissingContactField.subtitleUnknown',
								{ field: contactField?.join(', ') }
							),
						},
					});

					// eslint-disable-next-line no-console
					console.log('confirmPayment: No contact provided', contact);
					if (!sessionEndedRef.current) session.abort();
					return;
				}

				const applePayBillingContact = getAppleContact(billingContact);
				const lineItems: MakeApplePayPaymentRequest['lineItems'] = hasMultiFunds
					? multiFunds.map((fund: { Amount: string; FundKeyOrName: string }) => ({
							fundKey: fund.FundKeyOrName,
							amount: parseFloat(fund.Amount),
							amountWithFees: parseFloat(fund.Amount),
					  }))
					: [
							{
								fundKey: data.FundKeyOrName || '',
								amount: parseFloat(data.Amount || '0'),
								amountWithFees: parseFloat(data.Amount || '0'),
							},
					  ];
				const customFields: MakeApplePayPaymentRequest['customFields'] = [];
				Object.keys(customFieldKeys).forEach(key => {
					if (data[key]) {
						customFields.push({
							key,
							value: data[key],
						});
					}
				});

				const payload: MakeApplePayPaymentRequest = {
					applePayInfo: {
						applePayPaymentToken: {
							paymentData: JSON.stringify(paymentData),
							paymentMethod: {
								...paymentMethod,
								type: getApplePaymentMethodType(paymentMethod.type),
								paymentPass: {
									...paymentMethod.paymentPass,
									activationState: ApplePayPaymentPassActivationState.Activated,
								},
							},
							transactionIdentifier,
						},
						applePayBillingContact,
					},
					merchantKey: MerchantKey,
					customFields,
					lineItems,
					...contact.payer,
				};

				// eslint-disable-next-line no-console
				console.log('confirmPayment', { event, data, customFieldKeys, payload });

				const result: MakeApplePayPaymentResponse & { timedout?: boolean } = await Promise.race([
					fetch(APP_SETTINGS.applepayPaymentUrl, {
						method: 'POST',
						signal: controller.signal,
						headers: DEV_HEADERS,
						body: JSON.stringify(payload),
					}).then(res => res.json()),
					new Promise(resolve => {
						optimisticSuccessTimer = setTimeout(() => {
							resolve({ success: true, timedout: true });
							controller.abort();
						}, RESPONSE_TIMOUT);
					}),
				]);

				const hasInvalidFieldsError =
					result.status === QuickPayRelayResultStatus.InvalidInputFields &&
					(result.invalidFields || []) &&
					!!result.invalidFields.length;

				if (isApplePayPaymentSuccessful(result) && !hasInvalidFieldsError) {
					session.completePayment({ status: ApplePaySession.STATUS_SUCCESS });
					const tableItems = hasMultiFunds
						? multiFunds.map(fund => ({
								amount: fund.Amount,
								fundName: getFundName(fund.FundKeyOrName, Funds) || translation('success.noFundName'),
						  }))
						: [
								{
									amount: data.Amount || '0',
									fundName:
										getFundName(data.FundKeyOrName || '', Funds) ||
										translation('success.noFundName'),
								},
						  ];

					setSuccess({
						checkEmail: controller.signal.aborted || shouldCheckEmail(result),
						tableItems,
						emailAddress: contact.payer.emailAddress,
					});

					agent?.recordCustomEvent(WidgetId, {
						type: WidgetActions.applePaySheetClosed,
						success: true,
					});
				} else {
					session.completePayment({ status: ApplePaySession.STATUS_FAILURE });

					const message: ApplePayLocalError['message'] = {
						title: translation('applePayErrors.payment.title'),
						subtitle: translation('applePayErrors.payment.subtitle'),
					};

					if (hasInvalidFieldsError) {
						const translatedFields = result.invalidFields.map(
							(field: string) => invalidFieldErrorsTranslation[field]
						);

						message.title = translation('applePayErrors.invalidFields.title');
						message.subtitle = translation('applePayErrors.invalidFields.subtitle', {
							fields: translatedFields.join(', '),
						});
					}

					if (result.status === QuickPayRelayResultStatus.CardFail) {
						message.title = undefined;
						message.subtitle = translation('applePayErrors.cardFail.subtitle', {
							label: paymentLabel.NounLowerCase,
						});
					}

					if (result.status === QuickPayRelayResultStatus.InvalidAmount) {
						message.title = undefined;
						message.subtitle = translation('applePayErrors.invalidAmount.subtitle');
					}

					setAppleError({
						type: hasInvalidFieldsError
							? APPLEPAY_ERROR_TYPE.CONTACT_VALIDATION
							: APPLEPAY_ERROR_TYPE.PAYMENT_CONFIRMATION,
						message,
					});
					agent?.recordCustomEvent(WidgetId, {
						type: WidgetActions.applePaySheetClosed,
						success: false,
					});
				}
				sessionEndedRef.current = true;
			} catch (error) {
				if (!controller.signal.aborted && !sessionEndedRef.current) {
					// eslint-disable-next-line no-console
					console.log('While confirming payment - Aborting Apple Pay session:', error); // TODO: temporary. please remove
					session.abort();
				}
				// eslint-disable-next-line no-console
				console.log(error);

				setAppleError({
					type: APPLEPAY_ERROR_TYPE.UNKNOWN,
					message: {
						title: translation('applePayErrors.generic.title'),
						subtitle: translation('applePayErrors.generic.subtitle'),
					},
				});

				agent?.noticeError(WidgetErrors.applePaySheetClosedWithError, {
					error,
					abortedByTimer: controller.signal.aborted,
				});
				if (optimisticSuccessTimer) {
					clearTimeout(optimisticSuccessTimer);
				}
			}
		},
		[translation, MerchantKey, Funds, agent, invalidFieldErrorsTranslation, paymentLabel.NounLowerCase]
	);

	const makeApplePayment = useCallback(
		(data: WidgetInputsWithCustomFields, customFieldKeys: CustomFieldKeys = {}) => {
			if (status !== APPLEPAY_STATUS.AVAILABLE) {
				return;
			}
			if (!data.Amount || !data.FundKeyOrName) {
				setAppleError({
					type: APPLEPAY_ERROR_TYPE.FORM_VALIDATION,
					message: {
						title: translation('applePayErrors.form.title'),
						subtitle: translation('applePayErrors.form.subtitle'),
					},
				});
				return;
			}

			// Debugging only
			// eslint-disable-next-line no-console
			console.log('Handle ApplePayment', data);

			// Create the paymentRequest.
			const paymentRequest = getApplePayPaymentRequest(config.MerchantSettings, paymentLabel, data.Amount);

			if (!paymentRequest) {
				window.console.log('Apple Pay paymentRequest failed', data);
				setAppleError({
					type: APPLEPAY_ERROR_TYPE.PAYMENT_REQUEST,
					message: {
						title: translation('applePayErrors.paymentInfo.title'),
						subtitle: translation('applePayErrors.paymentInfo.subtitle'),
					},
				});
				agent?.noticeError(WidgetErrors.applePaymentRequestMissing, { paymentRequest });
				return;
			}

			// Create the Apple Pay session.
			const session = new ApplePaySession(SDK_VERSION, paymentRequest);
			sessionEndedRef.current = false;

			// Reset session phase tracking
			sessionPhaseRef.current = SessionPhase.INIT;

			try {
				// Setup handler for validation the merchant session.
				session.onvalidatemerchant = (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
					sessionPhaseRef.current = SessionPhase.VALIDATING;
					validateMerchant(session, event);
				};

				// Setup handler for payment method selection.
				session.onpaymentmethodselected = () => {
					sessionPhaseRef.current = SessionPhase.SHEET_SHOWN;
					session.completePaymentMethodSelection(
						paymentRequest.total,
						paymentRequest.lineItems as ApplePayJS.ApplePayLineItem[]
					);
				};

				// Setup handler for ShippingContact.
				session.onshippingcontactselected = () => {
					sessionPhaseRef.current = SessionPhase.SHEET_SHOWN;
					session.completeShippingContactSelection(
						ApplePaySession.STATUS_SUCCESS,
						paymentRequest.shippingMethods || [],
						paymentRequest.total,
						paymentRequest.lineItems as ApplePayJS.ApplePayLineItem[]
					);
				};

				// Setup handler for shipping method selection.
				session.onshippingmethodselected = () => {
					sessionPhaseRef.current = SessionPhase.SHEET_SHOWN;
					session.completeShippingMethodSelection(
						ApplePaySession.STATUS_SUCCESS,
						paymentRequest.total,
						paymentRequest.lineItems as ApplePayJS.ApplePayLineItem[]
					);
				};

				// Setup handler for cancellation.
				session.oncancel = (event: ApplePayJS.Event) => {
					// eslint-disable-next-line no-console
					console.error('ApplePay Sheet cancelled', event);
					sessionEndedRef.current = true;

					if (sessionPhaseRef.current === SessionPhase.INIT) {
						setAppleError({
							type: APPLEPAY_ERROR_TYPE.UNKNOWN,
							message: {
								title: translation('applePayErrors.systemSettings.title'),
								subtitle: translation('applePayErrors.systemSettings.subtitle'),
							},
						});
					}
				};

				// Setup handler for payment authorization.
				session.onpaymentauthorized = evt => {
					sessionPhaseRef.current = SessionPhase.PAYMENT_PROCESSING;
					confirmPayment(session, evt, data, customFieldKeys);
				};

				// Show the Apple Pay payment sheet.
				session.begin();
				agent?.recordCustomEvent(WidgetId, {
					type: WidgetActions.applePaySheetOpened,
				});
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error creating Apple Pay session:', error);

				setStatus(APPLEPAY_STATUS.UNAVAILABLE);

				if (session && !sessionEndedRef.current) {
					// eslint-disable-next-line no-console
					console.log('While creating payment - Aborting Apple Pay session:', error); // TODO: temporary. please remove
					session.abort();
				}

				setAppleError({
					type: APPLEPAY_ERROR_TYPE.UNKNOWN,
					message: {
						title: translation('applePayErrors.generic.title'),
						subtitle: translation('applePayErrors.generic.subtitle'),
					},
				});

				agent?.noticeError(WidgetErrors.applePaySessionError, { error });
			}
		},
		[status, agent, config.MerchantSettings, paymentLabel, translation, validateMerchant, confirmPayment]
	);

	useEffect(
		function validateApplePaySDK() {
			const checkForSDK = async () => {
				try {
					if (!window.ApplePaySession) {
						setStatus(APPLEPAY_STATUS.UNAVAILABLE);
						// Debugging only
						// eslint-disable-next-line no-console
						console.log('Apple Pay SDK: UNAVAILABLE');
					} else if (window.ApplePaySession && ApplePayMerchantId) {
						// Using version 1 method for checking Apple Pay availability
						const canPay = ApplePaySession.canMakePayments();
						setStatus(canPay ? APPLEPAY_STATUS.AVAILABLE : APPLEPAY_STATUS.UNAVAILABLE);
						// Debugging only
						// eslint-disable-next-line no-console
						console.log('Apple Pay SDK: canMakePaymentsWithActiveCard', {
							CanMakePayments: canPay,
						});
						// Going to log in New Relic - see PP-58660
					}
				} catch (error) {
					agent?.noticeError(WidgetErrors.applePaySDKCheckFailed, { error });
					setStatus(APPLEPAY_STATUS.UNAVAILABLE);
					// Debugging only
					// eslint-disable-next-line no-console
					console.error('Error verifying Apple Pay SDK:', error);
				}
			};

			if (!initiatedRef.current && status === APPLEPAY_STATUS.IDLE) {
				setStatus(APPLEPAY_STATUS.INITIALISING);
				initiatedRef.current = true;
				checkForSDK();
			}
		},
		[ApplePayMerchantId, agent, status]
	);

	return {
		applePayError: status === APPLEPAY_STATUS.AVAILABLE ? appleError : null,
		isAvailable: status === APPLEPAY_STATUS.AVAILABLE,
		makeApplePayment,
		clearAppleError: () => setAppleError(null),
		clearSuccess: () => setSuccess(null),
		success,
	};
}
